// @ts-nocheck
// Shared add-player logic used by BOTH the team page (`TeamPlayerActions`) and the
// division "Add Team" modal (`DivisionPage`), so the two can't drift:
//   - buildPlayerFieldColumns(registration): First/Last + registration.fields → AddForm columns
//   - extractPlayerInfoFromForm(formData):   AddForm `__info_*` keys → a plain info object
//   - derivePlayerName(info, idx):           name from first/last (fallback "Player N")
//   - buildPlayerPayload(info, refs, idx):   the `POST /api/v1/players/d` body
import _ from 'lodash';

// registration.fields fieldType → MUI DataGrid / AddForm column type.
const FIELD_TYPE_TO_COLUMN_TYPE = {
  date: 'date',
  number: 'number',
  boolean: 'boolean',
  'multi-select': 'multi-select',
  select: 'singleSelect',
  textbox: 'string',
};

// Build the player form columns: hard-coded First/Last name + optional Jersey #
// + every custom `registration.fields` entry (except first/last). AddForm keys
// are `__info_*` so extractPlayerInfoFromForm can peel them back into `info`.
export function buildPlayerFieldColumns(registration) {
  // Skip the built-in Jersey # column when the registration already collects a
  // number field of its own (it would render twice and fight over info.number).
  const hasCustomNumberField = _.some(
    _.get(registration, 'fields', []),
    (f) => f?.fieldId === 'number' || f?.fieldId === 'jerseyNumber',
  );
  return [
    {
      field: '__info_first_name', required: true, headerName: 'First Name', default: '', type: 'string', editable: true, width: 250,
      formData: { order: 1, type: 'text', required: true, unique: true, errorMsg: '', label: 'First Name' },
    },
    {
      field: '__info_last_name', required: true, headerName: 'Last Name', default: '', type: 'string', editable: true, width: 250,
      formData: { order: 2, type: 'text', required: true, unique: true, errorMsg: '', label: 'Last Name' },
    },
    // Jersey # feeds buildPlayerPayload's info.number → top-level Player.number
    // (a string field). Optional — stats-only orgs need it, others can skip it.
    ...(hasCustomNumberField ? [] : [{
      field: '__info_number', required: false, headerName: 'Jersey #', default: '', type: 'string', editable: true, width: 120,
      formData: { order: 2.5, type: 'text', required: false, unique: false, errorMsg: '', label: 'Jersey # (optional)' },
    }]),
    ..._.chain(_.get(registration, 'fields', []))
      .filter((f) => f?.fieldId !== 'first_name' && f?.fieldId !== 'last_name')
      .map((field, index) => ({
        field: `__info_${field.fieldId}`,
        required: field.required,
        headerName: field.name,
        valueOptions: field.options || [],
        default: '',
        type: FIELD_TYPE_TO_COLUMN_TYPE[field.fieldType] || 'string',
        editable: true,
        width: 150,
        formData: {
          order: Number(index) + 3,
          type: field.fieldType || 'text',
          required: Boolean(field.required),
          unique: false,
          errorMsg: '',
          options: field.options || [],
          label: field.name,
        },
      }))
      .value(),
  ];
}

// AddForm returns keys like `__info_first_name`; peel the prefix into a plain
// info object (`{ first_name, last_name, ...customFields }`).
export function extractPlayerInfoFromForm(formData) {
  const info = {};
  Object.keys(formData || {}).forEach((key) => {
    if (key.startsWith('__info_')) info[key.replace('__info_', '')] = formData[key];
  });
  return info;
}

// Player display name from an info object; falls back to "Player N" for nameless
// (excludeTeamName) rosters.
export function derivePlayerName(info, idx = 0) {
  if (info?.name) return info.name;
  const fn = info?.firstName || info?.first_name || '';
  const ln = info?.lastName || info?.last_name || '';
  const composed = `${fn} ${ln}`.trim();
  return composed || `Player ${(idx ?? 0) + 1}`;
}

// The `POST /api/v1/players/d` body. `refs` carries team/registration references
// (team, league, organization, tournament?, registration?, registrationTeam?).
// email/number are promoted to the top-level Player fields when present in info.
export function buildPlayerPayload(info, refs, idx = 0) {
  const safeInfo = info || {};
  const payload = {
    ...refs,
    name: derivePlayerName(safeInfo, idx),
    info: safeInfo,
  };
  const email = safeInfo.email;
  const number = safeInfo.number ?? safeInfo.jerseyNumber;
  if (email != null && email !== '') payload.email = email;
  if (number != null && number !== '') payload.number = number;
  return payload;
}
